import React, { useState } from "react";
import api from "../services/api";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  Autocomplete,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Alert,
  Chip,
  CircularProgress,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";

const fmt = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

const statusChip = (s) => {
  const map = {
    matched:   { label: "Matched", color: "success" },
    ambiguous: { label: "Pick one", color: "warning" },
    unknown:   { label: "Not found", color: "error" },
    missing:   { label: "Missing", color: "error" },
  };
  const m = map[s] || { label: s, color: "default" };
  return <Chip size="small" label={m.label} color={m.color} />;
};

export default function PurchaseInvoiceImport({ open, onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");

  const [vendor, setVendor] = useState(null);
  const [saveVendorAlias, setSaveVendorAlias] = useState(true);

  // Per-line resolution: products[i] = { Item_ID, Product_Description } and saveAlias flags
  const [lineProducts, setLineProducts] = useState([]);
  const [lineSaveAlias, setLineSaveAlias] = useState([]);

  const [allProducts, setAllProducts] = useState([]);
  const [allCustomers, setAllCustomers] = useState([]);

  const reset = () => {
    setFile(null); setPreview(null); setError("");
    setVendor(null); setLineProducts([]); setLineSaveAlias([]);
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/import/purchase/template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "PurchaseInvoice_Template.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError("Failed to download template");
    }
  };

  const downloadErrorReport = async () => {
    if (!preview?.lines?.length) return;
    try {
      const res = await api.post(
        "/import/errors/export",
        { kind: "purchase", lines: preview.lines },
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "PurchaseInvoice_Errors.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e?.response?.data?.error || "No errors to export");
    }
  };

  const upload = async () => {
    if (!file) return;
    setBusy(true); setError(""); setPreview(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/import/purchase/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(res.data);

      if (res.data?.header?.customer?.status === "matched") {
        setVendor(res.data.header.customer.item);
      }
      // pre-fill matched products per line
      const lines = res.data?.lines || [];
      setLineProducts(
        lines.map((l) => (l.product?.status === "matched" ? l.product.item : null))
      );
      setLineSaveAlias(lines.map(() => true));

      const [p, c] = await Promise.all([
        api.get("/products"),
        api.get("/accounts"),
      ]);
      setAllProducts(p.data || []);
      setAllCustomers(c.data || []);
    } catch (e) {
      setError(e?.response?.data?.error || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const setLineProduct = (i, value) => {
    setLineProducts((prev) => {
      const next = [...prev];
      next[i] = value;
      return next;
    });
  };

  const setLineAliasFlag = (i, value) => {
    setLineSaveAlias((prev) => {
      const next = [...prev];
      next[i] = value;
      return next;
    });
  };

  const allLinesResolved =
    preview && lineProducts.length === preview.lines.length &&
    lineProducts.every((p) => p?.Item_ID);

  const commit = async () => {
    if (!preview) return;
    if (!vendor?.id) {
      setError("Please pick a Vendor.");
      return;
    }
    if (!allLinesResolved) {
      setError("Resolve every line's product before saving.");
      return;
    }
    setBusy(true); setError("");
    try {
      const body = {
        header: {
          Customer_ID: vendor.id,
          Name:        vendor.SubAccount_Name,
          PO:          preview.header.poNumber,
          Notes:       preview.header.notes,
          Date:        preview.header.invoiceDate,
          saveCustomerAlias:
            saveVendorAlias && preview.header.vendorName !== vendor.SubAccount_Name
              ? preview.header.vendorName
              : null,
        },
        lines: preview.lines.map((l, i) => {
          const picked = lineProducts[i];
          return {
            Item_ID:          picked.Item_ID,
            Item_Description: picked.Product_Description,
            G_Wt: l.G_Wt,
            T_Wt: l.T_Wt,
            N_Wt: l.N_Wt,
            Rate: l.Rate,
            saveAlias:
              lineSaveAlias[i] && l.Item_Description &&
              l.Item_Description !== picked.Product_Description
                ? l.Item_Description
                : null,
          };
        }),
      };
      const res = await api.post("/import/purchase/commit", body);
      onImported?.(res.data?.Master_ID);
      reset();
      onClose();
    } catch (e) {
      setError(e?.response?.data?.error || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => { reset(); onClose(); }} fullWidth maxWidth="lg">
      <DialogTitle>Import Purchase Invoice from Excel</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {!preview && (
          <Box>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Header: Vendor / Date / PO / Notes. Each line gets its own
              Item Description, weights, and Rate. Products are matched per row,
              so a single invoice can mix Small Motors, Copper, etc.
            </Typography>

            <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
              <Button startIcon={<DownloadIcon />} variant="outlined" onClick={downloadTemplate}>
                Download Template
              </Button>
              <Button component="label" variant="contained" startIcon={<UploadFileIcon />}>
                Choose .xlsx
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  hidden
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                />
              </Button>
              {file && <Typography variant="body2">{file.name}</Typography>}
              <Button
                variant="contained"
                color="primary"
                disabled={!file || busy}
                onClick={upload}
              >
                {busy ? <CircularProgress size={20} /> : "Parse & Preview"}
              </Button>
            </Box>
          </Box>
        )}

        {preview && (
          <Box>
            <Typography variant="subtitle1" fontWeight="bold" mb={1}>Header</Typography>
            <Table size="small" sx={{ mb: 2 }}>
              <TableBody>
                <TableRow>
                  <TableCell sx={{ width: 180 }}>Vendor (file)</TableCell>
                  <TableCell>
                    {preview.header.vendorName || "—"} {statusChip(preview.header.customer.status)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Vendor (resolved)</TableCell>
                  <TableCell>
                    <Autocomplete
                      size="small"
                      options={allCustomers}
                      getOptionLabel={(o) => o?.SubAccount_Name || ""}
                      value={vendor}
                      onChange={(_, v) => setVendor(v)}
                      renderInput={(p) => <TextField {...p} placeholder="Pick vendor" />}
                      sx={{ width: 360 }}
                    />
                    {preview.header.customer.status !== "matched" && vendor && (
                      <FormControlLabel
                        sx={{ ml: 1 }}
                        control={
                          <Checkbox
                            size="small"
                            checked={saveVendorAlias}
                            onChange={(e) => setSaveVendorAlias(e.target.checked)}
                          />
                        }
                        label={`Save "${preview.header.vendorName}" as alias`}
                      />
                    )}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>{preview.header.invoiceDate || "—"}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>PO</TableCell>
                  <TableCell>{preview.header.poNumber || "—"}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Notes</TableCell>
                  <TableCell>{preview.header.notes || "—"}</TableCell>
                </TableRow>
              </TableBody>
            </Table>

            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                Line Items ({preview.lines.length})
                {preview.summary?.errorRows > 0 && (
                  <Chip
                    size="small"
                    color="error"
                    label={`${preview.summary.errorRows} row${preview.summary.errorRows === 1 ? "" : "s"} with issues`}
                    sx={{ ml: 1 }}
                  />
                )}
              </Typography>
              {preview.summary?.errorRows > 0 && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={downloadErrorReport}
                >
                  Download Error Report
                </Button>
              )}
            </Box>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Item (file)</TableCell>
                  <TableCell>Match</TableCell>
                  <TableCell sx={{ minWidth: 280 }}>Product (resolved)</TableCell>
                  <TableCell align="right">Net</TableCell>
                  <TableCell align="right">Rate</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Issues</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {preview.lines.map((l, i) => {
                  const picked = lineProducts[i];
                  const status = l.product?.status || "missing";
                  const showAlias =
                    status !== "matched" && picked && l.Item_Description &&
                    picked.Product_Description !== l.Item_Description;
                  return (
                    <TableRow key={i} sx={l._errors?.length ? { backgroundColor: "rgba(255,0,0,0.06)" } : undefined}>
                      <TableCell>{i + 1}</TableCell>
                      <TableCell>{l.Item_Description || "—"}</TableCell>
                      <TableCell>{statusChip(status)}</TableCell>
                      <TableCell>
                        <Autocomplete
                          size="small"
                          options={allProducts}
                          getOptionLabel={(o) => o?.Product_Description || ""}
                          value={picked || null}
                          onChange={(_, v) => setLineProduct(i, v)}
                          renderInput={(p) => <TextField {...p} placeholder="Pick product" />}
                        />
                        {showAlias && (
                          <FormControlLabel
                            sx={{ mt: 0.5 }}
                            control={
                              <Checkbox
                                size="small"
                                checked={lineSaveAlias[i] || false}
                                onChange={(e) => setLineAliasFlag(i, e.target.checked)}
                              />
                            }
                            label={`Save alias`}
                          />
                        )}
                      </TableCell>
                      <TableCell align="right">{fmt(l.N_Wt)}</TableCell>
                      <TableCell align="right">{fmt(l.Rate)}</TableCell>
                      <TableCell align="right">{fmt(l.N_Wt * l.Rate)}</TableCell>
                      <TableCell>
                        {l._errors?.length ? (
                          <Typography variant="caption" color="error">
                            {l._errors.join("; ")}
                          </Typography>
                        ) : (
                          <Chip size="small" color="success" label="OK" variant="outlined" />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
                <TableRow>
                  <TableCell colSpan={4} align="right" sx={{ fontWeight: "bold" }}>Totals</TableCell>
                  <TableCell align="right" sx={{ fontWeight: "bold" }}>{fmt(preview.totals.netWt)}</TableCell>
                  <TableCell />
                  <TableCell align="right" sx={{ fontWeight: "bold" }}>${fmt(preview.totals.amount)}</TableCell>
                  <TableCell />
                </TableRow>
              </TableBody>
            </Table>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={() => { reset(); onClose(); }}>Cancel</Button>
        {preview && <Button onClick={reset} disabled={busy}>Start Over</Button>}
        {preview && (
          <Button
            variant="contained"
            disabled={busy || !vendor?.id || !allLinesResolved}
            onClick={commit}
          >
            {busy ? <CircularProgress size={20} /> : "Save Purchase Invoice"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
